import type { ReactNode } from 'react'
import { cn } from './cn'

/**
 * Petit label capitalé posé au-dessus d'un titre ou d'un bloc.
 *
 * Plus discret que celui du design system (12px lavande) : dans l'app il sert de
 * label de section, pas d'accroche. D'où 11px en encre atténuée. Passer `accent`
 * quand il coiffe un bloc mis en avant, comme « Prochaine session ».
 */
export function Eyebrow({
  children,
  accent = false,
  className,
}: {
  children: ReactNode
  accent?: boolean
  className?: string
}) {
  return (
    <span
      className={cn(
        'text-[11px] font-semibold tracking-[0.08em] uppercase',
        accent ? 'text-lavender-deep' : 'text-ink-mute',
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Métadonnée : date, durée, auteur, mention sous un titre. */
export function Meta({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-[12.5px] text-ink-mute', className)}>{children}</span>
}

/**
 * Référence documentaire — n° de facture, de devis, de convention.
 * En chasse fixe pour que les colonnes de tableau s'alignent et que deux
 * références proches se distinguent au coup d'œil.
 */
export function Ref({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('font-mono text-[12.5px] text-ink-soft', className)}>{children}</span>
}

/**
 * Moment éditorial en Playfair Display : couverture, citation, gros chiffre de
 * certificat. Réservé — un titre d'écran courant reste en Outfit.
 */
export function Display({
  children,
  className,
  as: Tag = 'span',
}: {
  children: ReactNode
  className?: string
  as?: 'span' | 'h1' | 'h2' | 'p'
}) {
  return <Tag className={cn('u-display', className)}>{children}</Tag>
}

/**
 * Chiffre de KPI. 28px en usage courant, 42px pour le chiffre héros d'un écran.
 */
export function Stat({
  children,
  hero = false,
  className,
}: {
  children: ReactNode
  hero?: boolean
  className?: string
}) {
  return (
    <span
      className={cn(
        'block font-extrabold tracking-[-0.03em] tabular-nums',
        hero ? 'text-[42px] leading-none' : 'text-[28px] leading-tight',
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * Point d'attente. Le handoff proscrit les spinners : une pulsation de 1,5s sur
 * un point lavande, rien d'autre.
 */
export function Pending({ label = 'Chargement en cours' }: { label?: string }) {
  return (
    <span role="status" aria-label={label}>
      <span className="u-pending" />
    </span>
  )
}
